export const precalcLessons3 = [
  {
    id: 7,
    title: "Vectors & Parametric Equations",
    description: "Master vector operations, dot products, parametric curves, and applications in physics and geometry",
    vocabulary: [
      "Vector", "Scalar", "Magnitude", "Direction Angle", "Unit Vector", "Dot Product",
      "Component Form", "Resultant", "Parametric Equations", "Parameter", "Eliminating the Parameter"
    ],
    conceptExplanation: `**Vectors:**
A vector has both magnitude and direction.
Component form: v = ⟨a, b⟩
Magnitude: |v| = √(a² + b²)
Direction angle: θ = arctan(b/a)

**Vector Operations:**
• Addition: ⟨a,b⟩ + ⟨c,d⟩ = ⟨a+c, b+d⟩
• Subtraction: ⟨a,b⟩ - ⟨c,d⟩ = ⟨a-c, b-d⟩
• Scalar multiplication: k⟨a,b⟩ = ⟨ka, kb⟩
• Unit vector: û = v/|v|

**Dot Product:**
u · v = a₁a₂ + b₁b₂
|u||v|cosθ = u · v
• If u · v = 0, vectors are perpendicular
• If u · v > 0, angle is acute
• If u · v < 0, angle is obtuse

**Angle Between Vectors:**
cosθ = (u · v) / (|u| · |v|)

**Parametric Equations:**
Express x and y as functions of a parameter t:
x = f(t), y = g(t)
To eliminate the parameter: solve one equation for t, substitute into the other.

**Common Parametric Curves:**
• Line: x = x₀ + at, y = y₀ + bt
• Circle: x = r·cos(t), y = r·sin(t)
• Ellipse: x = a·cos(t), y = b·sin(t)

**Projectile Motion:**
x = v₀cos(θ)·t
y = v₀sin(θ)·t - (1/2)gt²`,
    examples: [
      {
        problem: "Find the magnitude and direction angle of v = ⟨3, -4⟩",
        solution: `Step 1: Find magnitude
|v| = √(3² + (-4)²) = √(9 + 16) = √25 = 5

Step 2: Find direction angle
θ = arctan(-4/3) ≈ -53.1°
Since the vector is in QIV (positive x, negative y):
θ ≈ -53.1° or equivalently 306.9°

Answer: |v| = 5, θ ≈ -53.1° (or 306.9°)`
      },
      {
        problem: "Find the angle between u = ⟨2, 1⟩ and v = ⟨1, 3⟩",
        solution: `Step 1: Compute dot product
u · v = (2)(1) + (1)(3) = 2 + 3 = 5

Step 2: Compute magnitudes
|u| = √(4 + 1) = √5
|v| = √(1 + 9) = √10

Step 3: Apply angle formula
cosθ = (u · v)/(|u||v|) = 5/(√5 · √10) = 5/√50 = 5/(5√2) = 1/√2

θ = arccos(1/√2) = 45°

Answer: θ = 45°`
      },
      {
        problem: "Eliminate the parameter: x = t + 1, y = t² - 2",
        solution: `Step 1: Solve for t from the x equation
x = t + 1 → t = x - 1

Step 2: Substitute into y equation
y = t² - 2 = (x - 1)² - 2
y = x² - 2x + 1 - 2
y = x² - 2x - 1

Answer: y = x² - 2x - 1 (a parabola)`
      }
    ],
    teacherTip: "Vectors are everywhere in physics and engineering! The dot product is your tool for finding angles and checking perpendicularity. Remember: two vectors are perpendicular if and only if their dot product is zero. For parametric equations, think of t as time — the curve traces out a path as t increases. When eliminating the parameter, always check if there are domain restrictions on t that limit the curve.",
    questions: [
      { id: 1, question: "Find |v| if v = ⟨5, 12⟩", options: ["13", "17", "7", "√169"], correctAnswer: "13", solution: "|v| = √(25 + 144) = √169 = 13." },
      { id: 2, question: "Find the unit vector in the direction of v = ⟨3, 4⟩", options: ["⟨3/5, 4/5⟩", "⟨3, 4⟩/5", "⟨1/3, 1/4⟩", "⟨4/5, 3/5⟩"], correctAnswer: "⟨3/5, 4/5⟩", solution: "|v| = 5. û = v/|v| = ⟨3/5, 4/5⟩." },
      { id: 3, question: "Add: ⟨2, -3⟩ + ⟨-1, 5⟩", options: ["⟨1, 2⟩", "⟨3, -8⟩", "⟨1, -2⟩", "⟨3, 2⟩"], correctAnswer: "⟨1, 2⟩", solution: "⟨2+(-1), -3+5⟩ = ⟨1, 2⟩." },
      { id: 4, question: "Compute: 3⟨2, -1⟩", options: ["⟨6, -3⟩", "⟨5, 2⟩", "⟨6, 3⟩", "⟨2, -3⟩"], correctAnswer: "⟨6, -3⟩", solution: "3⟨2, -1⟩ = ⟨6, -3⟩." },
      { id: 5, question: "Compute the dot product: ⟨3, 4⟩ · ⟨2, -1⟩", options: ["2", "10", "14", "-2"], correctAnswer: "2", solution: "3(2) + 4(-1) = 6 - 4 = 2." },
      { id: 6, question: "Are ⟨3, 4⟩ and ⟨-4, 3⟩ perpendicular?", options: ["Yes", "No", "Only if scaled", "Cannot determine"], correctAnswer: "Yes", solution: "Dot product: 3(-4) + 4(3) = -12 + 12 = 0. Since dot product = 0, they are perpendicular." },
      { id: 7, question: "Find the angle between ⟨1, 0⟩ and ⟨0, 1⟩", options: ["90°", "45°", "0°", "180°"], correctAnswer: "90°", solution: "Dot product = 0. cosθ = 0 → θ = 90°. These are the standard basis vectors, perpendicular." },
      { id: 8, question: "Eliminate the parameter: x = 2t, y = t + 3", options: ["y = x/2 + 3", "y = 2x + 3", "y = x + 3", "y = x/2 - 3"], correctAnswer: "y = x/2 + 3", solution: "t = x/2. y = x/2 + 3." },
      { id: 9, question: "Eliminate the parameter: x = cos(t), y = sin(t)", options: ["x² + y² = 1", "y = x", "x² - y² = 1", "y = x²"], correctAnswer: "x² + y² = 1", solution: "cos²t + sin²t = 1 → x² + y² = 1 (unit circle)." },
      { id: 10, question: "Find the direction angle of v = ⟨1, 1⟩", options: ["45°", "90°", "30°", "60°"], correctAnswer: "45°", solution: "θ = arctan(1/1) = arctan(1) = 45°." },
      { id: 11, question: "Find the direction angle of v = ⟨0, 5⟩", options: ["90°", "0°", "45°", "180°"], correctAnswer: "90°", solution: "The vector points straight up (positive y-axis). θ = 90°." },
      { id: 12, question: "Which vector is parallel to ⟨2, 3⟩?", options: ["⟨4, 6⟩", "⟨3, 2⟩", "⟨-3, 2⟩", "⟨2, -3⟩"], correctAnswer: "⟨4, 6⟩", solution: "⟨4, 6⟩ = 2⟨2, 3⟩ — a scalar multiple, so parallel." },
      { id: 13, question: "Find the resultant of forces ⟨5, 0⟩ and ⟨0, 12⟩", options: ["⟨5, 12⟩ with magnitude 13", "⟨5, 12⟩ with magnitude 17", "⟨12, 5⟩", "magnitude 5"], correctAnswer: "⟨5, 12⟩ with magnitude 13", solution: "Resultant = ⟨5+0, 0+12⟩ = ⟨5, 12⟩. |v| = √(25+144) = 13." },
      { id: 14, question: "Parametric equations x = 3cos(t), y = 3sin(t) describe:", options: ["Circle of radius 3", "Ellipse", "Parabola", "Line"], correctAnswer: "Circle of radius 3", solution: "x² + y² = 9cos²t + 9sin²t = 9. This is a circle of radius 3." },
      { id: 15, question: "Parametric equations x = 4cos(t), y = 2sin(t) describe:", options: ["Ellipse", "Circle", "Hyperbola", "Parabola"], correctAnswer: "Ellipse", solution: "x²/16 + y²/4 = cos²t + sin²t = 1. This is an ellipse." },
      { id: 16, question: "Find the projection of u = ⟨3, 4⟩ onto v = ⟨1, 0⟩", options: ["⟨3, 0⟩", "⟨0, 4⟩", "⟨3, 4⟩", "⟨4, 0⟩"], correctAnswer: "⟨3, 0⟩", solution: "proj_v u = (u·v/|v|²)v = (3/1)⟨1,0⟩ = ⟨3, 0⟩." },
      { id: 17, question: "A vector has magnitude 10 and direction angle 30°. Its component form is:", options: ["⟨5√3, 5⟩", "⟨5, 5√3⟩", "⟨10, 30⟩", "⟨√3, 1⟩"], correctAnswer: "⟨5√3, 5⟩", solution: "x = 10cos30° = 10(√3/2) = 5√3. y = 10sin30° = 10(1/2) = 5. v = ⟨5√3, 5⟩." },
      { id: 18, question: "Subtract: ⟨5, 2⟩ - ⟨3, -1⟩", options: ["⟨2, 3⟩", "⟨8, 1⟩", "⟨2, 1⟩", "⟨-2, 3⟩"], correctAnswer: "⟨2, 3⟩", solution: "⟨5-3, 2-(-1)⟩ = ⟨2, 3⟩." },
      { id: 19, question: "If u · v = |u||v|, what is the angle between u and v?", options: ["0°", "90°", "180°", "45°"], correctAnswer: "0°", solution: "cosθ = (u·v)/(|u||v|) = 1 → θ = 0°. The vectors point in the same direction." },
      { id: 20, question: "If u · v = -|u||v|, what is the angle between u and v?", options: ["180°", "0°", "90°", "270°"], correctAnswer: "180°", solution: "cosθ = -1 → θ = 180°. The vectors point in opposite directions." },
      { id: 21, question: "Find |2u - v| if u = ⟨1, 2⟩ and v = ⟨3, 0⟩", options: ["√5", "5", "√17", "√13"], correctAnswer: "√5", solution: "2u = ⟨2,4⟩. 2u - v = ⟨2-3, 4-0⟩ = ⟨-1, 4⟩. |⟨-1,4⟩| = √(1+16) = √17." },
      { id: 22, question: "Eliminate the parameter: x = t², y = t³", options: ["y = x^(3/2)", "y = x³/²", "y² = x³", "y = x^(2/3)"], correctAnswer: "y² = x³", solution: "t = x^(1/2). y = (x^(1/2))³ = x^(3/2). So y² = x³." },
      { id: 23, question: "A projectile is launched at 45° with v₀ = 20 m/s. The horizontal velocity component is:", options: ["10√2 m/s", "20 m/s", "10 m/s", "20√2 m/s"], correctAnswer: "10√2 m/s", solution: "vₓ = v₀cos45° = 20·(√2/2) = 10√2 m/s." },
      { id: 24, question: "Which operation produces a scalar from two vectors?", options: ["Dot product", "Cross product", "Vector addition", "Scalar multiplication"], correctAnswer: "Dot product", solution: "The dot product u·v = a₁a₂ + b₁b₂ produces a scalar (number)." },
      { id: 25, question: "Find the angle between ⟨1, 0⟩ and ⟨1, 1⟩", options: ["45°", "90°", "30°", "60°"], correctAnswer: "45°", solution: "cosθ = (1·1+0·1)/(1·√2) = 1/√2 → θ = 45°." },
      { id: 26, question: "The zero vector ⟨0, 0⟩ is:", options: ["Perpendicular to every vector", "Parallel to every vector", "Both A and B", "Neither"], correctAnswer: "Both A and B", solution: "The zero vector has no defined direction, so it is considered both parallel and perpendicular to every vector by convention." },
      { id: 27, question: "Find the component form of a vector from point A(1,2) to point B(4,6)", options: ["⟨3, 4⟩", "⟨-3, -4⟩", "⟨5, 8⟩", "⟨4, 6⟩"], correctAnswer: "⟨3, 4⟩", solution: "v = B - A = ⟨4-1, 6-2⟩ = ⟨3, 4⟩." },
      { id: 28, question: "Parametric equations x = 1 + 2t, y = 3 - t describe:", options: ["A line", "A parabola", "A circle", "An ellipse"], correctAnswer: "A line", solution: "Linear parametric equations describe a straight line. Eliminating t: t = (x-1)/2, y = 3 - (x-1)/2 = (7-x)/2." },
      { id: 29, question: "Find the work done by force F = ⟨3, 4⟩ over displacement d = ⟨2, 1⟩", options: ["10", "14", "5", "7"], correctAnswer: "10", solution: "Work = F · d = 3(2) + 4(1) = 6 + 4 = 10." },
      { id: 30, question: "Which is NOT a property of the dot product?", options: ["u · v = v · u", "u · (v + w) = u·v + u·w", "u · v is a vector", "(cu) · v = c(u·v)"], correctAnswer: "u · v is a vector", solution: "The dot product produces a scalar, not a vector. All other properties listed are valid." }
    ]
  },
  {
    id: 8,
    title: "Limits & Introduction to Calculus",
    description: "Understand limits intuitively and formally, continuity, and the bridge to differential calculus",
    vocabulary: [
      "Limit", "One-Sided Limit", "Continuity", "Removable Discontinuity", "Jump Discontinuity",
      "Infinite Discontinuity", "Squeeze Theorem", "Indeterminate Form", "L'Hôpital's Rule", "Derivative"
    ],
    conceptExplanation: `**The Concept of a Limit:**
lim(x→c) f(x) = L means f(x) gets arbitrarily close to L as x approaches c.
The limit does NOT depend on f(c) — only on values near c.

**One-Sided Limits:**
• Left-hand limit: lim(x→c⁻) f(x)
• Right-hand limit: lim(x→c⁺) f(x)
• Two-sided limit exists ⟺ both one-sided limits exist and are equal

**Limit Laws:**
• lim[f(x) ± g(x)] = lim f(x) ± lim g(x)
• lim[f(x)·g(x)] = lim f(x) · lim g(x)
• lim[f(x)/g(x)] = lim f(x) / lim g(x), if lim g(x) ≠ 0
• lim[f(x)]ⁿ = [lim f(x)]ⁿ

**Continuity:**
f is continuous at x = c if:
1. f(c) is defined
2. lim(x→c) f(x) exists
3. lim(x→c) f(x) = f(c)

**Types of Discontinuity:**
• Removable: hole in graph (limit exists, but f(c) ≠ limit)
• Jump: left and right limits exist but are unequal
• Infinite: vertical asymptote

**Indeterminate Forms:**
0/0, ∞/∞ — need algebraic manipulation or L'Hôpital's Rule

**The Derivative (Preview):**
f'(x) = lim(h→0) [f(x+h) - f(x)] / h
This is the instantaneous rate of change — the slope of the tangent line.

**Special Limits:**
lim(x→0) sinx/x = 1
lim(x→0) (1-cosx)/x = 0
lim(x→∞) (1 + 1/x)^x = e`,
    examples: [
      {
        problem: "Evaluate: lim(x→3) (x² - 9)/(x - 3)",
        solution: `Step 1: Check for indeterminate form
Direct substitution: (9-9)/(3-3) = 0/0 — indeterminate

Step 2: Factor and simplify
(x² - 9)/(x - 3) = (x-3)(x+3)/(x-3) = x + 3 (for x ≠ 3)

Step 3: Take the limit
lim(x→3) (x + 3) = 3 + 3 = 6

Answer: 6`
      },
      {
        problem: "Determine if f(x) = {x² if x < 2; 4 if x = 2; x + 3 if x > 2} is continuous at x = 2",
        solution: `Step 1: Check f(2)
f(2) = 4 ✓ (defined)

Step 2: Find left-hand limit
lim(x→2⁻) x² = 4

Step 3: Find right-hand limit
lim(x→2⁺) (x + 3) = 5

Step 4: Compare
Left limit (4) ≠ Right limit (5)
The two-sided limit does not exist.

Answer: f is NOT continuous at x = 2 (jump discontinuity)`
      },
      {
        problem: "Use the limit definition to find f'(x) for f(x) = x²",
        solution: `Step 1: Set up the difference quotient
f'(x) = lim(h→0) [f(x+h) - f(x)] / h
= lim(h→0) [(x+h)² - x²] / h

Step 2: Expand
= lim(h→0) [x² + 2xh + h² - x²] / h
= lim(h→0) [2xh + h²] / h
= lim(h→0) h(2x + h) / h
= lim(h→0) (2x + h)

Step 3: Evaluate
= 2x + 0 = 2x

Answer: f'(x) = 2x`
      }
    ],
    teacherTip: "Limits are the foundation of all calculus! When you get 0/0, don't panic — it just means you need to do more algebra (factor, rationalize, or use trig identities). The limit exists only when BOTH one-sided limits agree. For continuity, think of it as 'you can draw the graph without lifting your pencil.' The derivative definition lim(h→0)[f(x+h)-f(x)]/h is the most important formula in calculus — understand it deeply!",
    questions: [
      { id: 1, question: "Evaluate: lim(x→4) (x² - 16)/(x - 4)", options: ["8", "0", "4", "Undefined"], correctAnswer: "8", solution: "Factor: (x-4)(x+4)/(x-4) = x+4. lim(x→4)(x+4) = 8." },
      { id: 2, question: "Evaluate: lim(x→2) (3x + 1)", options: ["7", "6", "5", "8"], correctAnswer: "7", solution: "Direct substitution: 3(2) + 1 = 7." },
      { id: 3, question: "Evaluate: lim(x→0) sinx/x", options: ["1", "0", "∞", "Undefined"], correctAnswer: "1", solution: "This is a special limit: lim(x→0) sinx/x = 1." },
      { id: 4, question: "Evaluate: lim(x→∞) 1/x", options: ["0", "1", "∞", "-∞"], correctAnswer: "0", solution: "As x → ∞, 1/x → 0." },
      { id: 5, question: "Evaluate: lim(x→∞) (3x² + 2)/(x² - 1)", options: ["3", "0", "∞", "2"], correctAnswer: "3", solution: "Divide by x²: (3 + 2/x²)/(1 - 1/x²) → 3/1 = 3 as x → ∞." },
      { id: 6, question: "Evaluate: lim(x→0) (x² + 3x)/x", options: ["3", "0", "∞", "1"], correctAnswer: "3", solution: "Factor: x(x+3)/x = x+3. lim(x→0)(x+3) = 3." },
      { id: 7, question: "For f to be continuous at x = c, which must be true?", options: ["All three: f(c) defined, limit exists, limit = f(c)", "Only f(c) is defined", "Only the limit exists", "f(c) = 0"], correctAnswer: "All three: f(c) defined, limit exists, limit = f(c)", solution: "Continuity requires: (1) f(c) defined, (2) limit exists, (3) limit equals f(c)." },
      { id: 8, question: "What type of discontinuity has a 'hole' in the graph?", options: ["Removable", "Jump", "Infinite", "Essential"], correctAnswer: "Removable", solution: "A removable discontinuity (hole) occurs when the limit exists but f(c) is undefined or ≠ limit." },
      { id: 9, question: "Evaluate: lim(x→3⁺) 1/(x-3)", options: ["+∞", "-∞", "0", "1"], correctAnswer: "+∞", solution: "As x → 3⁺, (x-3) → 0⁺, so 1/(x-3) → +∞." },
      { id: 10, question: "Evaluate: lim(x→3⁻) 1/(x-3)", options: ["-∞", "+∞", "0", "1"], correctAnswer: "-∞", solution: "As x → 3⁻, (x-3) → 0⁻, so 1/(x-3) → -∞." },
      { id: 11, question: "Does lim(x→3) 1/(x-3) exist?", options: ["No, one-sided limits differ", "Yes, it equals ∞", "Yes, it equals 0", "Yes, it equals 1"], correctAnswer: "No, one-sided limits differ", solution: "Left limit = -∞, right limit = +∞. They differ, so the two-sided limit does not exist." },
      { id: 12, question: "Evaluate: lim(x→0) (√(x+4) - 2)/x", options: ["1/4", "0", "1/2", "∞"], correctAnswer: "1/4", solution: "Rationalize: multiply by (√(x+4)+2)/(√(x+4)+2). Numerator: x+4-4=x. Result: x/[x(√(x+4)+2)] = 1/(√(x+4)+2) → 1/4." },
      { id: 13, question: "Using the limit definition, f'(x) for f(x) = 3x is:", options: ["3", "3x", "0", "1"], correctAnswer: "3", solution: "[f(x+h)-f(x)]/h = [3(x+h)-3x]/h = 3h/h = 3. lim(h→0) 3 = 3." },
      { id: 14, question: "Using the limit definition, f'(x) for f(x) = x³ is:", options: ["3x²", "x²", "3x", "2x"], correctAnswer: "3x²", solution: "[(x+h)³-x³]/h = [3x²h+3xh²+h³]/h = 3x²+3xh+h² → 3x² as h→0." },
      { id: 15, question: "Evaluate: lim(x→1) (x³ - 1)/(x - 1)", options: ["3", "0", "1", "∞"], correctAnswer: "3", solution: "Factor: (x-1)(x²+x+1)/(x-1) = x²+x+1. At x=1: 1+1+1=3." },
      { id: 16, question: "Which is an indeterminate form?", options: ["0/0", "1/0", "0/1", "∞/1"], correctAnswer: "0/0", solution: "0/0 is indeterminate — it requires further analysis. 1/0 is undefined (not indeterminate)." },
      { id: 17, question: "Evaluate: lim(x→∞) (5x³ - 2x)/(2x³ + 1)", options: ["5/2", "0", "∞", "-2"], correctAnswer: "5/2", solution: "Divide by x³: (5 - 2/x²)/(2 + 1/x³) → 5/2 as x → ∞." },
      { id: 18, question: "A function has lim(x→2⁻)f(x) = 3 and lim(x→2⁺)f(x) = 3 but f(2) = 5. This is:", options: ["Removable discontinuity", "Jump discontinuity", "Infinite discontinuity", "Continuous"], correctAnswer: "Removable discontinuity", solution: "The limit exists (= 3) but f(2) = 5 ≠ 3. This is a removable discontinuity." },
      { id: 19, question: "Evaluate: lim(x→0) (1 - cosx)/x", options: ["0", "1", "∞", "1/2"], correctAnswer: "0", solution: "Special limit: lim(x→0)(1-cosx)/x = 0." },
      { id: 20, question: "The slope of the tangent line to f(x) at x = a is:", options: ["f'(a)", "f(a)", "[f(a+h)-f(a)]/h", "f(a)/a"], correctAnswer: "f'(a)", solution: "The derivative f'(a) gives the slope of the tangent line at x = a." },
      { id: 21, question: "Evaluate: lim(x→-∞) e^x", options: ["0", "∞", "1", "-∞"], correctAnswer: "0", solution: "As x → -∞, e^x → 0 (exponential decay)." },
      { id: 22, question: "Evaluate: lim(x→∞) e^(-x)", options: ["0", "∞", "1", "e"], correctAnswer: "0", solution: "e^(-x) = 1/e^x → 0 as x → ∞." },
      { id: 23, question: "Is f(x) = 1/x continuous on (-∞, 0) ∪ (0, ∞)?", options: ["Yes", "No, has infinite discontinuity at x=0", "No, has jump discontinuity", "No, has removable discontinuity"], correctAnswer: "Yes", solution: "On each interval (-∞,0) and (0,∞), f(x) = 1/x is continuous. The discontinuity at x=0 is outside these intervals." },
      { id: 24, question: "Evaluate: lim(x→2) (x² - 4)/(x² - 3x + 2)", options: ["4", "0", "∞", "2"], correctAnswer: "4", solution: "Factor: (x-2)(x+2)/[(x-2)(x-1)] = (x+2)/(x-1). At x=2: 4/1 = 4." },
      { id: 25, question: "The Intermediate Value Theorem states that if f is continuous on [a,b] and N is between f(a) and f(b), then:", options: ["There exists c in (a,b) with f(c) = N", "f(a) = f(b)", "f is differentiable", "N = 0"], correctAnswer: "There exists c in (a,b) with f(c) = N", solution: "IVT: A continuous function on [a,b] takes every value between f(a) and f(b)." },
      { id: 26, question: "Evaluate: lim(x→0) x·sin(1/x)", options: ["0", "1", "∞", "Does not exist"], correctAnswer: "0", solution: "By Squeeze Theorem: -|x| ≤ x·sin(1/x) ≤ |x|. Since |x| → 0, the limit = 0." },
      { id: 27, question: "Which polynomial is always continuous?", options: ["All polynomials", "Only linear polynomials", "Only constant polynomials", "None"], correctAnswer: "All polynomials", solution: "All polynomial functions are continuous everywhere on ℝ." },
      { id: 28, question: "Evaluate: lim(x→4) √x", options: ["2", "4", "√4", "16"], correctAnswer: "2", solution: "√x is continuous at x=4. Direct substitution: √4 = 2." },
      { id: 29, question: "The derivative represents:", options: ["Instantaneous rate of change", "Average rate of change", "Total change", "Accumulated change"], correctAnswer: "Instantaneous rate of change", solution: "The derivative f'(x) = lim(h→0)[f(x+h)-f(x)]/h is the instantaneous rate of change." },
      { id: 30, question: "Evaluate: lim(x→∞) (2x + 1)/(x + 3)", options: ["2", "1", "0", "∞"], correctAnswer: "2", solution: "Divide by x: (2 + 1/x)/(1 + 3/x) → 2/1 = 2 as x → ∞." }
    ]
  }
];
